import { FieldValues } from "react-hook-form";
import {
  LinkPayload,
  NotePayload,
  ProspectListPayload,
} from "src/models/prospect.model";
import { urlReplace } from "src/utils/common.utils";

import API_URL from "./apiConfig/api.config";
import handleResponse from "./apiConfig/api.response.service";
import apiCall from "./apiConfig/apiCall.config";

export async function getProspects(params: ProspectListPayload) {
  const res = await apiCall.get(API_URL.PROSPECT.GET_PROSPECT_LIST, {
    ...params,
  });
  const responseData = await handleResponse(res);

  return responseData;
}

export async function getProspectDetails(
  prospectUUid: string,
  searchParams?: URLSearchParams
) {
  const res = await apiCall.get(
    `${API_URL.PROSPECT.GET_PROSPECT}${prospectUUid}${searchParams ? "?" + searchParams?.toString() : ""}`
  );
  const responseData = await handleResponse(res);

  return responseData;
}

export async function createProspect(typeUUid: string, data: FieldValues) {
  const res = await apiCall.post(
    `${API_URL.PROSPECT.ADD_PROSPECT}${typeUUid}`,
    data
  );
  const responseData = await handleResponse(res);

  return responseData;
}

export async function editProspect(prospectUuid: string, data: FieldValues) {
  const res = await apiCall.patch(
    `${API_URL.PROSPECT.ADD_PROSPECT}${prospectUuid}`,
    data
  );
  const responseData = await handleResponse(res);

  return responseData;
}

export async function deleteProspect(prospectUuid: string, reason: string) {
  const res = await apiCall.removeWithBody(
    `${API_URL.PROSPECT.DELETE_PROSPECT}${prospectUuid}`,
    { reason: reason }
  );
  const responseData = await handleResponse(res);

  return responseData;
}

export async function doNotDisturb(prospectUuid: string, dnd: boolean) {
  const res = await apiCall.patch(
    `${API_URL.PROSPECT.DO_NOT_DISTURB}${prospectUuid}/dnd`,
    { dnd }
  );
  const responseData = await handleResponse(res);

  return responseData;
}

export async function getProspectNotes(
  prospectUuid: string,
  params: { [key: string]: string | number | number[] } | undefined
) {
  const url = urlReplace(
    API_URL.PROSPECT.GET_NOTES,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.get(url, params);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function addNote(prospectUuid: string, body: NotePayload) {
  const url = urlReplace(
    API_URL.PROSPECT.ADD_NOTES,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.post(url, body);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function updateNote(noteUuid: string, body: NotePayload) {
  const res = await apiCall.patch(
    `${API_URL.PROSPECT.UPDATE_NOTES}${noteUuid}`,
    body
  );
  const responseData = await handleResponse(res);

  return responseData;
}

export async function linkProspect(prospectUuid: string, body: LinkPayload) {
  const url = urlReplace(API_URL.PROSPECT.LINK, "prospectUuid", prospectUuid);
  const res = await apiCall.patch(url, body);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function getAllProfessionalList(
  payload: { [key: string]: string | number | number[] } | undefined
) {
  const res = await apiCall.get(
    API_URL.PROSPECT.GET_ALL_PROFESSIONAL_LIST,
    payload
  );
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData;
  }
}

export async function getProspectLogs(prospectUuid: string) {
  const url = urlReplace(
    API_URL.PROSPECT.GET_PROSPECT_LOGS,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.get(url);
  const responseData = await handleResponse(res);
  if (responseData.ok) {
    return responseData;
  }
}

export async function markAsHotLead(prospectUuid: string, isHotLead: boolean) {
  const url = urlReplace(
    API_URL.PROSPECT.MARK_AS_HOT_LEAD,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.patch(url, { hot_lead: isHotLead });
  const responseData = await handleResponse(res);

  return responseData;
}

export async function getProspectContacts(prospectUuid: string) {
  const url = urlReplace(
    API_URL.PROSPECT.GET_PROSPECT_CONTACTS,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.get(url);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function createProspectContact(
  prospectUuid: string,
  body: FieldValues
) {
  const url = urlReplace(
    API_URL.PROSPECT.ADD_PROSPECT_CONTACT,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.post(url, body);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function editProspectContact(
  contactUuid: string,
  body: FieldValues
) {
  const url = API_URL.PROSPECT.EDIT_PROSPECT_CONTACT.replace(
    "{contactUuid}",
    contactUuid
  );
  const res = await apiCall.patch(url, body);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function patchProspectOffice(
  prospectUuid: string,
  body: import("src/models/prospect.model").PatchProspectOfficePayload
) {
  const url = urlReplace(
    API_URL.PROSPECT.ADD_OFFICE_INFO,
    "prospectUuid",
    prospectUuid
  );
  const res = await apiCall.patch(url, body);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function deleteProspectContact(contactUuid: string) {
  const url = API_URL.PROSPECT.DELETE_PROSPECT_CONTACT.replace(
    "{contactUuid}",
    contactUuid
  );
  const res = await apiCall.patch(url);
  const responseData = await handleResponse(res);

  return responseData;
}

export async function updateProspectContactStatus(
  contactUuid: string,
  status: number | boolean
) {
  const url = API_URL.PROSPECT.PROSPECT_CONTACT_ACTIVITY_STATUS;
  const res = await apiCall.patch(url, {
    contact_uuid: contactUuid,
    activity_status: status,
  });
  const responseData = await handleResponse(res);

  return responseData;
}

export async function switchProspectContact(
  contactUuid: string,
  status: number | boolean
) {
  const url = API_URL.PROSPECT.PROSPECT_CONTACT_ACTIVITY_STATUS;
  const res = await apiCall.patch(url, {
    contact_uuid: contactUuid,
    activity_status: status,
  });
  const responseData = await handleResponse(res);

  return responseData;
}

export async function downloadProspectImportTemplate(typeUuid: string) {
  const url = urlReplace(
    API_URL.PROSPECT.DOWNLOAD_IMPORT_TEMPLATE,
    "typeUuid",
    typeUuid
  );
  return apiCall.getBlobFile(url);
}

export async function importProspectExcel(typeUuid: string, file: File) {
  const formData = new FormData();
  formData.append("typeUuid", typeUuid);
  formData.append("file", file);

  const res = await apiCall.fileUploadApi(
    API_URL.PROSPECT.IMPORT_EXCEL,
    formData
  );
  return handleResponse(res);
}

export async function getProspectImportLogs() {
  const res = await apiCall.get(API_URL.PROSPECT.IMPORT_LOG);
  return handleResponse(res);
}

export async function getProspectImportErrors(uuid: string) {
  const url = urlReplace(API_URL.PROSPECT.IMPORT_ERRORS, "uuid", uuid);
  const res = await apiCall.get(url);
  return handleResponse(res);
}

export async function initiateProspectImport(uuid: string) {
  const url = urlReplace(API_URL.PROSPECT.IMPORT_INITIATE, "uuid", uuid);
  const res = await apiCall.patch(url, {});
  return handleResponse(res);
}

export async function downloadProspectImportFile(uuid: string) {
  const url = urlReplace(API_URL.PROSPECT.IMPORT_FILE, "uuid", uuid);
  return apiCall.getBlobFile(url);
}
